import { Injectable } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import en from '@translations/en/resourcesui.json';
import de from '@translations/de/resourcesui.json';
import fr from '@translations/fr/resourcesui.json';
import it from '@translations/it/resourcesui.json';
import { environment } from '@env/environment';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */
export function extract(s: string) {
  return s;
}

@Injectable()
export class I18nService {
  defaultLanguage: string;
  supportedLanguages: string[];

  constructor(private translateService: TranslateService) {
    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en', en);
    translateService.setTranslation('de', de);
    translateService.setTranslation('fr', fr);
    translateService.setTranslation('it', it);

    this.supportedLanguages = environment.supportedLanguages;
    this.language = this.defaultLanguage = environment.defaultLanguage;
  }
  
  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {

    let isSupportedLanguage = this.supportedLanguages.indexOf(language) > -1;
    // console.log(`isSupportedLanguage: ${isSupportedLanguage}, language: '${language}'`);

    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language = this.supportedLanguages.find(supportedLanguage => supportedLanguage.startsWith(language)) || '';
      isSupportedLanguage = Boolean(language);
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this.defaultLanguage;
    }

    this.translateService.setDefaultLang(language);
    this.translateService.use(language);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translateService.currentLang;
  }
}
